import type { CalendarEvent } from '../types'
import { parseEventDate } from './time'

/**
 * Slot height in pixels (configurable)
 */
export const DEFAULT_SLOT_HEIGHT = 50

/**
 * Calculate top position for an event based on its start time
 */
export function calculateEventTop(
  event: CalendarEvent,
  startHour: number,
  slotDuration: number,
  slotHeight: number = DEFAULT_SLOT_HEIGHT
): number {
  const eventStart = parseEventDate(event.start)
  const eventHour = eventStart.getHours()
  const eventMinute = eventStart.getMinutes()

  const slotsPerHour = 60 / slotDuration
  const slotIndex = (eventHour - startHour) * slotsPerHour + (eventMinute / slotDuration)

  return slotIndex * slotHeight
}

/**
 * Calculate height for an event based on its duration
 */
export function calculateEventHeight(
  event: CalendarEvent,
  slotDuration: number,
  slotHeight: number = DEFAULT_SLOT_HEIGHT
): number {
  const slotsNeeded = event.duration / slotDuration
  return slotsNeeded * slotHeight
}

/**
 * Calculate event style (top, height) for rendering
 */
export function calculateEventStyle(
  event: CalendarEvent,
  startHour: number,
  slotDuration: number,
  slotHeight: number = DEFAULT_SLOT_HEIGHT
): { top: number; height: number } {
  return {
    top: calculateEventTop(event, startHour, slotDuration, slotHeight),
    height: calculateEventHeight(event, slotDuration, slotHeight),
  }
}

/**
 * Get time from slot position (for drag-drop)
 */
export function getTimeFromPosition(
  yPosition: number,
  startHour: number,
  slotDuration: number,
  slotHeight: number = DEFAULT_SLOT_HEIGHT
): { hour: number; minute: number } {
  const slotIndex = Math.floor(yPosition / slotHeight)
  const totalMinutes = startHour * 60 + slotIndex * slotDuration
  const hour = Math.floor(totalMinutes / 60)
  const minute = totalMinutes % 60

  return { hour, minute }
}

/**
 * Snap position to nearest slot
 */
export function snapToSlot(
  yPosition: number,
  slotHeight: number = DEFAULT_SLOT_HEIGHT
): number {
  return Math.round(yPosition / slotHeight) * slotHeight
}

/**
 * Calculate new start time after drag
 */
export function calculateNewStartTime(
  _originalEvent: CalendarEvent,
  newDate: Date,
  newHour: number,
  newMinute: number
): Date {
  const result = new Date(newDate)
  result.setHours(newHour, newMinute, 0, 0)
  return result
}

/**
 * Check if a position is within calendar bounds
 */
export function isWithinBounds(
  yPosition: number,
  startHour: number,
  endHour: number,
  slotDuration: number,
  slotHeight: number = DEFAULT_SLOT_HEIGHT
): boolean {
  const totalSlots = ((endHour - startHour) * 60) / slotDuration
  const maxPosition = totalSlots * slotHeight

  return yPosition >= 0 && yPosition < maxPosition
}

/**
 * Calculate column width for week view
 */
export function calculateDayColumnWidth(containerWidth: number, numDays: number = 7): number {
  return containerWidth / numDays
}

/**
 * Get day index from x position (for week view)
 */
export function getDayIndexFromPosition(
  xPosition: number,
  containerWidth: number,
  numDays: number = 7
): number {
  const columnWidth = calculateDayColumnWidth(containerWidth, numDays)
  return Math.floor(xPosition / columnWidth)
}

// ==================== Event Overlap Handling ====================

export interface EventLayout {
  event: CalendarEvent
  column: number
  totalColumns: number
  top: number
  height: number
}

/**
 * Check if two events overlap in time
 */
export function eventsOverlap(event1: CalendarEvent, event2: CalendarEvent): boolean {
  const start1 = parseEventDate(event1.start)
  const end1 = new Date(start1.getTime() + event1.duration * 60000)
  const start2 = parseEventDate(event2.start)
  const end2 = new Date(start2.getTime() + event2.duration * 60000)

  return start1 < end2 && start2 < end1
}

/**
 * Group events by day for processing
 */
function groupEventsByDay(events: CalendarEvent[]): Map<string, CalendarEvent[]> {
  const groups = new Map<string, CalendarEvent[]>()

  for (const event of events) {
    const date = parseEventDate(event.start)
    const dayKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`

    if (!groups.has(dayKey)) {
      groups.set(dayKey, [])
    }
    groups.get(dayKey)!.push(event)
  }

  return groups
}

/**
 * Calculate overlap layout for events
 * This algorithm positions overlapping events side-by-side in columns
 */
export function calculateOverlapLayout(
  events: CalendarEvent[],
  startHour: number,
  slotDuration: number,
  slotHeight: number = DEFAULT_SLOT_HEIGHT
): EventLayout[] {
  if (events.length === 0) return []

  const layouts: EventLayout[] = []

  // Group events by day first
  const dayGroups = groupEventsByDay(events)

  // Process each day separately
  for (const dayEvents of dayGroups.values()) {
    // Sort events by start time, then by duration (longer first for better packing)
    const sorted = [...dayEvents].sort((a, b) => {
      const startA = parseEventDate(a.start).getTime()
      const startB = parseEventDate(b.start).getTime()
      if (startA !== startB) return startA - startB
      return b.duration - a.duration
    })

    // Track which columns are occupied at each time
    const columns: CalendarEvent[][] = []

    for (const event of sorted) {
      // Find the first available column for this event
      let targetColumn = 0
      let placed = false

      while (!placed) {
        if (!columns[targetColumn]) {
          columns[targetColumn] = []
        }

        // Check if this event overlaps with any event in this column
        const hasOverlap = columns[targetColumn].some((existingEvent) =>
          eventsOverlap(event, existingEvent)
        )

        if (!hasOverlap) {
          columns[targetColumn].push(event)
          placed = true
        } else {
          targetColumn++
        }
      }
    }

    // Now calculate the total number of columns needed for each group
    // Events that overlap need to know about each other's columns
    const eventToColumn = new Map<string, number>()
    const eventToMaxColumns = new Map<string, number>()

    // First pass: assign columns
    for (let col = 0; col < columns.length; col++) {
      for (const event of columns[col]) {
        eventToColumn.set(event.id, col)
      }
    }

    // Second pass: calculate max columns for each event
    for (const event of sorted) {
      let maxColumns = 1

      // Find all events that overlap with this one
      for (const otherEvent of sorted) {
        if (event.id !== otherEvent.id && eventsOverlap(event, otherEvent)) {
          const otherColumn = eventToColumn.get(otherEvent.id)!
          maxColumns = Math.max(maxColumns, otherColumn + 1)
        }
      }

      // The event itself is in a column
      const eventColumn = eventToColumn.get(event.id)!
      maxColumns = Math.max(maxColumns, eventColumn + 1)

      eventToMaxColumns.set(event.id, maxColumns)
    }

    // Create layouts
    for (const event of sorted) {
      const column = eventToColumn.get(event.id)!
      const totalColumns = eventToMaxColumns.get(event.id)!

      layouts.push({
        event,
        column,
        totalColumns,
        top: calculateEventTop(event, startHour, slotDuration, slotHeight),
        height: calculateEventHeight(event, slotDuration, slotHeight),
      })
    }
  }

  return layouts
}

/**
 * Get style object for an event with overlap handling
 */
export function getOverlapEventStyle(layout: EventLayout): {
  top: number
  height: number
  left: string
  width: string
} {
  const widthPercent = 100 / layout.totalColumns
  const leftPercent = (layout.column * 100) / layout.totalColumns

  return {
    top: layout.top,
    height: layout.height,
    left: `${leftPercent}%`,
    width: `${widthPercent}%`,
  }
}
